import React, { useEffect, useMemo, useState } from "react";
import TableSearch from "../../../../../CommonElements/HeaderTableSearch";
import {
  Action,
  approvalLeaveModal,
  Days,
  LeaveType,
  light,
  NA,
  Name,
  Reason,
  SearchTableButton,
  Status,
} from "../../../../../utils/Constant";
import { TableColumn } from "react-data-table-component";
import BadgeDetails from "../../../../../CommonElements/Badge/BadgeDetails";
import { getStatus } from "../../../../../utils/getStatus";
import IconComponent from "../../../../../CommonElements/IconComponent";
import { ModalType } from "../../../../../Types/UserProfileDetails";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import CustomDataTable from "../../../../../CommonElements/Table/CustomTable";
import { ILeaveData } from "../../types";
import { useStaggeredRowStyles } from "../../../../../hooks/useStaggeredRowStyle";
import SkeletonTable from "../../../../../CommonElements/Loader/SkeletonTableLoader";
import { dateConversion } from "../../../../../utils/dateUtils";

export interface ITeamMembersLeaveRequestTableProps {
  filterText: string;
  toggleCleared: boolean;
  onRowSelected: any;
  teamLeaveRequestList: ILeaveData[];
  teamLeaveRequestListLoading: boolean;
  setFilterText: (value: string) => void;
  openModal: (
    modalType: ModalType,
    employeeInfo: ILeaveData,
    leaveId: number
  ) => void;
}

const TeamMembersLeaveRequestTable = ({
  openModal,
  filterText,
  setFilterText,
  toggleCleared,
  onRowSelected,
  teamLeaveRequestList,
  teamLeaveRequestListLoading,
}: ITeamMembersLeaveRequestTableProps) => {
  const { darkMode } = useAppSelector(({ layout }) => layout);
  const [animate, setAnimate] = useState(false);


  const tableData = useMemo<(ILeaveData & { _rowClass: string })[]>(() => {
    return teamLeaveRequestList.map((row, i) => ({
      ...row,
      _rowClass: `row-animate row-delay-${i}`,
    }));
  }, [teamLeaveRequestList]);

  const filterData = useMemo(() => {
    return (
      tableData?.filter(
        (d) =>
          d.team_member.name
            .toLowerCase()
            ?.replaceAll(" ", "")
            .includes(filterText?.toLowerCase()?.replaceAll(" ", "")) ||
          d.team_member.email
            ?.toLowerCase()
            ?.replaceAll(" ", "")
            .includes(filterText?.toLowerCase()?.replaceAll(" ", ""))
      ) || []
    );
  }, [tableData, filterText]);

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <TableSearch
        value={filterText}
        label={SearchTableButton}
        containerClassName="dataTables_filter d-flex align-items-center"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFilterText(e.target.value)
        }
      />
    );
  }, [filterText]);

  const columns = useMemo<TableColumn<ILeaveData>[]>(() => {
    const baseCols: TableColumn<ILeaveData>[] = [
      {
        name: "#",
        cell: (_, index) => index + 1,
        sortable: false,
        width: "70px",
      },
      {
        name: Name,
        selector: (row) => row.team_member.name,
        sortable: true,
        width: "180px",
      },
      {
        name: "Start & End Date",
        cell: (row) => (
          <BadgeDetails
            color={light}
            className={`badget-light rounded-pill f-12 ${
              darkMode ? "text-white" : "text-dark"
            }`}
            text={
              row.start_date === row.end_date
                ? dateConversion(row.start_date, false, true)
                : `${dateConversion(
                    row.start_date,
                    false,
                    true
                  )} - ${dateConversion(row.end_date, false, true)}`
            }
          />
        ),
        sortable: true,
        width: "230px",
      },
      {
        name: "Comp Off Date",
        cell: (row) => (
          <BadgeDetails
            color={light}
            className={`badget-light rounded-pill f-12 ${
              darkMode ? "text-white" : "text-dark"
            }`}
            text={dateConversion(row.against_compoff_date, false, true)}
          />
        ),
        sortable: true,
        width: "150px",
      },
      {
        name: LeaveType,
        selector: (row) => row.leave_type,
        sortable: true,
        width: "140px",
      },
      {
        name: Days,
        selector: (row) => (row.total_days != null ? row.total_days : NA),
        sortable: true,
        width: "90px",
      },

      {
        name: Status,
        cell: (row) => (
          <BadgeDetails
            className="text-capitalize f-12"
            color={getStatus(row.status)}
            text={row.status}
          />
        ),
        sortable: true,
        width: "100px",
      },

      {
        name: Action,
        cell: (row) => (
          <IconComponent
            className="fa fa-edit text-primary f-16 pointer"
            onClick={() => openModal(approvalLeaveModal, row, row.leave_id)}
          />
        ),
        width: "80px",
      },
      {
        name: Reason,
        selector: (row) => row.reason,
        sortable: true,
        width: "180px",
      },
    ];
    return baseCols;
  }, [tableData, filterText]);

  useEffect(() => {
    if (!teamLeaveRequestListLoading && teamLeaveRequestList.length > 0) {
      const timeout = setTimeout(() => setAnimate(true), 50);
      return () => clearTimeout(timeout);
    }
    setAnimate(false); // only reset if data is truly empty
  }, [teamLeaveRequestListLoading, teamLeaveRequestList]);

  return (
    <div>
      <CustomDataTable
        striped
        subHeader
        highlightOnHover
        data={filterData || []}
        columns={columns}
        subHeaderComponent={subHeaderComponentMemo}
        selectableRows
        toggleCleared={toggleCleared}
        onSelectRowsChange={onRowSelected}
        loading={teamLeaveRequestListLoading}
        loaderComponent={
          <SkeletonTable
            rows={tableData.length || 10}
            columns={columns}
            rowHeight={40}
            gap={4}
          />
        }
      />
    </div>
  );
};

export default TeamMembersLeaveRequestTable;
